import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const formatCurrency = (value) => money.format(Number.isFinite(value) ? value : 0);
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const getStatusClasses = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'paid' || value === 'completed') return 'bg-emerald-100 text-emerald-800';
  if (value === 'partial' || value === 'partially_paid') return 'bg-amber-100 text-amber-900';
  if (value === 'overdue' || value === 'declined' || value === 'failed') return 'bg-rose-100 text-rose-800';
  return 'bg-slate-100 text-slate-800';
};

const resolveInvoiceFromResponse = (payload) => {
  const body = payload?.data ?? payload ?? {};
  if (!body || typeof body !== 'object') return null;
  if (body.success === true && body.data && typeof body.data === 'object') return body.data;
  if (body.data && typeof body.data === 'object' && (body.data.uuid || body.data.invoiceNumber || body.data.invoice_number)) {
    return body.data;
  }
  if (body.uuid || body.invoiceNumber || body.invoice_number) return body;
  return null;
};

const extractInvoiceShareToken = (data) => {
  if (!data || typeof data !== 'object') return null;
  const t =
    data.invoice_share_token ||
    data.invoice_public_token ||
    data.public_invoice_token ||
    data.share_token ||
    data.invoice?.share_token ||
    data.Invoice?.share_token ||
    (data.linked_invoice && (data.linked_invoice.share_token || data.linked_invoice.public_token));
  return t && String(t).trim() ? String(t).trim() : null;
};

const shareTokenFromResolveResponse = (res) => {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  const d = body.data;
  return (
    extractInvoiceShareToken(d) ||
    extractInvoiceShareToken(body) ||
    (typeof d?.share_token === 'string' && d.share_token.trim()) ||
    (typeof body.share_token === 'string' && body.share_token.trim()) ||
    null
  );
};

const getLineDescription = (line) =>
  line?.description ||
  line?.inventory?.name ||
  line?.Inventory?.name ||
  '—';

async function fetchInvoiceShareTokenByQuoteShare(shareToken) {
  const enc = encodeURIComponent(shareToken);
  const paths = [
    `/invoice/quotes/public/${enc}/invoice-share`,
    `/invoice/quotes/public/${enc}/linked-invoice`,
    `/invoice/from-quote-share/${enc}`,
  ];
  for (const path of paths) {
    try {
      const res = await axios.get(`${apiUrl}${path}`);
      const tok = shareTokenFromResolveResponse(res);
      if (tok) return tok;
    } catch (_) {
      // try next
    }
  }
  return null;
}

const InvoicePublicView = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [companyLegalName, setCompanyLegalName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const [invRes, brandRes] = await Promise.allSettled([
          axios.get(`${apiUrl}/invoice/public/${token}`),
          axios.get(`${apiUrl}/settings/branding`),
        ]);
        let resolvedInvoice =
          invRes.status === 'fulfilled' ? resolveInvoiceFromResponse(invRes.value) : null;
        if (!resolvedInvoice) {
          const resolvedToken = await fetchInvoiceShareTokenByQuoteShare(token);
          if (resolvedToken) {
            const retryRes = await axios.get(`${apiUrl}/invoice/public/${resolvedToken}`);
            resolvedInvoice = resolveInvoiceFromResponse(retryRes);
          }
        }
        if (resolvedInvoice) {
          setInvoice(resolvedInvoice);
        } else {
          setError(invRes.value?.data?.message || 'Invoice not found or this link has expired.');
        }
        if (brandRes.status === 'fulfilled' && brandRes.value.data) {
          const b = brandRes.value.data;
          if (b.logo_url) setLogoUrl(b.logo_url);
          if (b.company_legal_name) setCompanyLegalName(b.company_legal_name);
        }
      } catch (err) {
        setError('This invoice link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [token]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="max-w-md text-center p-8 bg-white rounded-lg shadow-md">
          <div className="text-5xl mb-4">&#128274;</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Invoice Not Available</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const customer = invoice.Customer || {};
  const lines = invoice.InvoiceLines || [];
  const total = parseFloat(invoice.total_amount) || 0;
  const totalPaid = parseFloat(invoice.total_paid) || 0;
  const balanceDue = invoice.balance_due != null ? parseFloat(invoice.balance_due) : Math.max(0, total - totalPaid);
  const payments = Array.isArray(invoice.payments) ? invoice.payments : [];
  const subtotal = lines.reduce((s, l) => s + parseFloat(l.quantity) * parseFloat(l.unit_price), 0);
  const discountValue = parseFloat(invoice.discount_value) || 0;
  const discountAmt = invoice.discount_type === 'percent' ? subtotal * discountValue / 100 : discountValue;
  const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number || '—';
  const displayStatus = String(invoice.status || 'unknown').replace(/_/g, ' ');

  return (
    <main className="min-h-screen bg-slate-100 px-3 py-6 sm:px-6 sm:py-10 print:bg-white print:px-0 print:py-0">
      <article className="mx-auto w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60 print:max-w-none print:rounded-none print:border-0 print:shadow-none">
        <header className="border-b border-slate-200 bg-gradient-to-r from-blue-700 via-blue-700 to-indigo-700 px-4 py-6 text-white sm:px-8 sm:py-8 print:bg-white print:text-slate-900">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-4">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Company logo"
                  className="h-14 w-auto max-w-36 rounded-md bg-white/95 p-1 object-contain ring-1 ring-white/50"
                />
              )}
              <div>
                {companyLegalName ? (
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-100 print:text-slate-600">
                    {companyLegalName}
                  </p>
                ) : null}
                <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Invoice</h1>
                <p className="mt-1 text-sm text-blue-100 print:text-slate-500">#{invoiceNumber}</p>
              </div>
            </div>
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusClasses(invoice.status)} print:border print:border-slate-300`}>
              {displayStatus}
            </span>
          </div>
        </header>

        <div className="space-y-6 px-4 py-6 sm:space-y-8 sm:px-8 sm:py-8">
          <section aria-labelledby="invoice-overview-heading" className="rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
            <h2 id="invoice-overview-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Invoice Overview
            </h2>
            <dl className="mt-3 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="text-slate-500">Invoice Date</dt>
                <dd className="mt-1 font-semibold text-slate-900">{formatDate(invoice.date)}</dd>
              </div>
              {invoice.due_date && (
                <div>
                  <dt className="text-slate-500">Due Date</dt>
                  <dd className="mt-1 font-semibold text-slate-900">{formatDate(invoice.due_date)}</dd>
                </div>
              )}
              <div>
                <dt className="text-slate-500">Status</dt>
                <dd className="mt-1 font-semibold capitalize text-slate-900">{displayStatus}</dd>
              </div>
            </dl>
          </section>

          <section aria-labelledby="billed-to-heading" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <h2 id="billed-to-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Billed To
            </h2>
            <p className="mt-2 text-lg font-semibold text-slate-900">
              {customer.firstname} {customer.lastname}
            </p>
            {customer.email && <p className="mt-1 text-sm text-slate-700">{customer.email}</p>}
            {customer.phone && <p className="text-sm text-slate-700">{customer.phone}</p>}
          </section>

          <section aria-labelledby="line-items-heading" className="space-y-3">
            <h2 id="line-items-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Line Items
            </h2>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-[640px] w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Description</th>
                    <th className="w-20 px-4 py-3 text-right font-semibold text-slate-700">Qty</th>
                    <th className="w-36 px-4 py-3 text-right font-semibold text-slate-700">Unit Price</th>
                    <th className="w-36 px-4 py-3 text-right font-semibold text-slate-700">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center italic text-slate-500">No line items</td>
                    </tr>
                  ) : (
                    lines.map((line, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-slate-900">{getLineDescription(line)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{parseFloat(line.quantity)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{formatCurrency(parseFloat(line.unit_price))}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {formatCurrency(parseFloat(line.quantity) * parseFloat(line.unit_price))}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="border-t border-slate-200">
                    <td colSpan={3} className="px-4 py-2 text-right text-sm text-slate-600">Subtotal</td>
                    <td className="px-4 py-2 text-right text-sm font-medium text-slate-800">{formatCurrency(subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="px-4 py-1 text-right text-sm text-slate-600">
                      Discount {invoice.discount_type === 'percent' ? `(${discountValue}%)` : ''}
                    </td>
                    <td className={`px-4 py-1 text-right text-sm font-medium ${discountAmt > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                      - {formatCurrency(discountAmt)}
                    </td>
                  </tr>
                  <tr className="border-t-2 border-slate-300">
                    <td colSpan={3} className="px-4 py-3 text-right text-base font-bold text-slate-900">Total</td>
                    <td className="px-4 py-3 text-right text-xl font-bold text-blue-700">{formatCurrency(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {invoice.notes && (
            <section aria-labelledby="invoice-notes-heading" className="rounded-xl border border-blue-200 bg-blue-50 p-4 sm:p-5">
              <h2 id="invoice-notes-heading" className="text-sm font-semibold uppercase tracking-wide text-blue-800">
                Notes
              </h2>
              <p className="mt-2 whitespace-pre-line text-sm text-slate-800">{invoice.notes}</p>
            </section>
          )}

          <section aria-labelledby="payment-summary-heading" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <h2 id="payment-summary-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Payment Summary
            </h2>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-slate-600">Invoice Total</span>
                <span className="font-semibold text-slate-900">{formatCurrency(total)}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
                <span className="text-emerald-800">Paid To Date</span>
                <span className="font-semibold text-emerald-800">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-3">
                <span className="font-semibold text-slate-800">Balance Due</span>
                <span className={`text-lg font-bold ${balanceDue > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                  {formatCurrency(balanceDue)}
                </span>
              </div>
              {invoice.last_payment_date && (
                <div className="sm:col-span-2 flex items-center justify-between text-slate-700">
                  <span className="text-slate-500">Last Payment</span>
                  <span className="font-medium text-slate-900">{formatDate(invoice.last_payment_date)}</span>
                </div>
              )}
            </div>
          </section>

          <section aria-labelledby="payment-history-heading" className="space-y-3">
            <h2 id="payment-history-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Payment History
            </h2>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              {payments.length === 0 ? (
                <p className="px-4 py-6 text-sm italic text-slate-500">No payments recorded yet.</p>
              ) : (
                <table className="min-w-[640px] w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Date</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Amount</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Method</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((p) => (
                      <tr key={p.uuid}>
                        <td className="px-4 py-3 text-slate-700">{formatDate(p.date || p.createdAt)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {formatCurrency(parseFloat(p.amount || 0))}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusClasses(p.status)}`}>
                            {String(p.status || '—').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{p.method || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
        <footer className="border-t border-slate-200 bg-slate-50 px-4 py-4 sm:px-8 print:hidden">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            aria-label="Print or save this invoice as a PDF"
          >
            <span aria-hidden="true">&#128438;</span>
            <span>Print / Save PDF</span>
          </button>
        </footer>
      </article>
    </main>
  );
};

export default InvoicePublicView;
